import React, { useId } from 'react';
import { ArrowLeft, RefreshCw, Eye, X, AlertCircle } from 'lucide-react';
import { historyLines, historyDiff, formatSigned, formatUkDate } from './stockTakeLogic';

const StockTakeHistory = ({
  historyData,
  isLoadingHistory,
  historyError,
  selectedDocument,
  setSelectedDocument,
  historySearchTerm,
  setHistorySearchTerm,
  loadHistory
}) => {
  const searchId = useId();

  return (
    <div className="stock-take-history">
      {isLoadingHistory ? (
        <div className="skeleton-table st-history-skeleton" aria-busy="true" aria-label="Loading history">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="skeleton-table-row">
              <div className="skeleton-table-cell skeleton st-skel-wide" />
              <div className="skeleton-table-cell skeleton st-skel-wide" />
              <div className="skeleton-table-cell skeleton st-skel-mid" />
              <div className="skeleton-table-cell skeleton st-skel-btn" />
            </div>
          ))}
        </div>
      ) : selectedDocument ? (
        // Document detail view
        (() => {
          const items = historyLines(selectedDocument);
          const totalDiscrepancies = items.filter(item => historyDiff(item) !== 0).length;
          const increaseCount = items.filter(item => historyDiff(item) > 0).length;
          const decreaseCount = items.filter(item => historyDiff(item) < 0).length;
          const noChangeCount = items.filter(item => historyDiff(item) === 0).length;

          return (
            <div className="document-detail">
              <button
                type="button"
                className="back-to-history-btn"
                onClick={() => setSelectedDocument(null)}
              >
                <ArrowLeft size={16} aria-hidden="true" /> Back to History
              </button>
              <div className="document-header">
                <h3>Document: {selectedDocument.DOCNO}</h3>
                <p className="document-date">
                  Date: {formatUkDate(selectedDocument.CURDATE)} &middot; {items.length} line{items.length !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Discrepancy Summary - each figure is a number of lines */}
              <div className="discrepancy-summary">
                <h4>Discrepancy Summary</h4>
                <div className="discrepancy-stats">
                  <div className="stat-box total">
                    <span className="stat-value">{totalDiscrepancies}</span>
                    <span className="stat-label">Lines adjusted</span>
                  </div>
                  <div className="stat-box increase">
                    <span className="stat-value">{increaseCount}</span>
                    <span className="stat-label">Lines increased</span>
                  </div>
                  <div className="stat-box decrease">
                    <span className="stat-value">{decreaseCount}</span>
                    <span className="stat-label">Lines decreased</span>
                  </div>
                  {noChangeCount > 0 && (
                    <div className="stat-box no-change">
                      <span className="stat-value">{noChangeCount}</span>
                      <span className="stat-label">Lines unchanged</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="document-items">
                <h4>Items Detail</h4>
                {items.length > 0 ? (
                  <div className="st-history-detail-wrap">
                    <table className="history-items-table detailed">
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th className="number-col">Before</th>
                          <th className="number-col">Counted</th>
                          <th className="number-col">Adjustment</th>
                          <th className="number-col">After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, idx) => {
                          // Support both adjustments format and TRANSORDER_C_SUBFORM format
                          const before = item.CURRENT ?? '-';
                          const counted = item.SCANNED ?? '-';
                          const adjustment = historyDiff(item);
                          const after = typeof before === 'number' ? before + adjustment : (typeof counted === 'number' ? counted : '-');

                          return (
                            <tr key={idx} className={adjustment < 0 ? 'row-decrease' : adjustment > 0 ? 'row-increase' : ''}>
                              <td>
                                <div className="item-info">
                                  <span className="item-desc">{item.PARTDES || item.PARTNAME || '-'}</span>
                                  <span className="item-barcode">{item.BARCODE || '-'}</span>
                                </div>
                              </td>
                              <td className="number-col" data-label="Before">{before}</td>
                              <td className="number-col" data-label="Counted">{counted}</td>
                              <td
                                className={`number-col adjustment ${adjustment < 0 ? 'negative' : adjustment > 0 ? 'positive' : 'zero'}`}
                                data-label="Adjustment"
                              >
                                {formatSigned(adjustment)}
                              </td>
                              <td className="number-col after" data-label="After">{after}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p>No items found</p>
                )}
              </div>
            </div>
          );
        })()
      ) : (
        // Document list view
        <>
          <div className="history-header">
            <h3>Submitted Stock Takes</h3>
            <button type="button" className="refresh-history-btn" onClick={loadHistory}>
              <RefreshCw size={16} aria-hidden="true" /> Refresh
            </button>
          </div>

          {historyError ? (
            <div className="st-history-error" role="alert">
              <AlertCircle size={28} aria-hidden="true" />
              <p><strong>Could not load the history.</strong> {historyError}</p>
              <button type="button" className="st-btn st-btn-primary" onClick={loadHistory}>
                <RefreshCw size={18} aria-hidden="true" /> Retry
              </button>
            </div>
          ) : (
            <>
              {/* Search Bar */}
              <div className="history-search">
                <label htmlFor={searchId} className="sr-only">Search stock takes</label>
                <input
                  id={searchId}
                  type="text"
                  className="history-search-input"
                  placeholder="Search by document number or date (dd/mm/yyyy)..."
                  value={historySearchTerm}
                  onChange={(e) => setHistorySearchTerm(e.target.value)}
                />
                {historySearchTerm && (
                  <button
                    type="button"
                    className="history-search-clear"
                    onClick={() => setHistorySearchTerm('')}
                    aria-label="Clear search"
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                )}
              </div>

              {historyData.length === 0 ? (
                <div className="history-empty">
                  <p>No stock take documents found.</p>
                </div>
              ) : (
                <div className="history-list">
                  {(() => {
                    const filteredHistory = historyData.filter(doc => {
                      if (!historySearchTerm) return true;
                      const searchLower = historySearchTerm.toLowerCase();
                      const docNo = (doc.DOCNO || '').toLowerCase();
                      const dateStr = doc.CURDATE ? formatUkDate(doc.CURDATE) : '';
                      return docNo.includes(searchLower) || dateStr.includes(searchLower);
                    });

                    if (filteredHistory.length === 0) {
                      return (
                        <div className="history-empty">
                          <p>No documents match "{historySearchTerm}"</p>
                        </div>
                      );
                    }

                    return (
                      <table className="history-table">
                        <thead>
                          <tr>
                            <th>Document #</th>
                            <th>Date</th>
                            <th>Lines</th>
                            <th><span className="sr-only">Action</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredHistory.map((doc, idx) => (
                            <tr key={doc.DOCNO || idx}>
                              <td className="doc-number">{doc.DOCNO}</td>
                              <td>{formatUkDate(doc.CURDATE)}</td>
                              <td>{historyLines(doc).length}</td>
                              <td>
                                <button
                                  type="button"
                                  className="view-doc-btn"
                                  onClick={() => setSelectedDocument(doc)}
                                  aria-label={`View ${doc.DOCNO}`}
                                >
                                  <Eye size={16} aria-hidden="true" /> View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default StockTakeHistory;
